import type { FC } from "react";
import React, { useState } from "react";
import {
  Trans,
  useRouter,
  useTracking,
  useTranslation,
} from "@edreamsodigeo/frontend-core";
import {
  Body,
  Box,
  Flex,
  Glyph,
  PopoverContainer,
  Link,
} from "@edreamsodigeo/prisma-design-system";

import { PRIME_LANDING_PAGE_URL } from "~/components/acquisition/HeaderPrimeButton/config/constants";
import useGetSubscriptionOffers from "~/clientState/hooks/useGetSubscriptionOffers";
import {
  SubscriptionOfferPeriod,
  SubscriptionOfferType,
} from "~/generated/graphql";
import { useFilterOfferByType } from "~/clientState/hooks/useFilterOfferByType";
import { freeTrialDays } from "~/tools/subscriptionUtils";
import type { LightBoxFooterProps } from "~/components/acquisition/Lightbox/LightboxRevamp/components/LightboxFooter/contracts";
import { LightboxFooterPopover } from "~/components/acquisition/Lightbox/LightboxRevamp/components/LightboxFooter/components/LightboxFooterPopover";
import { LightboxFooterButtons } from "~/components/acquisition/Lightbox/LightboxRevamp/components/LightboxFooter/components/LightboxFooterButtons";
import { LinkComponent } from "./components/LinkComponent";
import TrustPilotWidget from "~/components/common/TrustPilotWidget";
import { TrustPilotType } from "~/components/common/TrustPilotWidget/contract";
import {
  ActionsEnum,
  getCategoryLabel,
  trackingLabels,
} from "../../config/tracking";

const LightboxFooter: FC<LightBoxFooterProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { trackEvent } = useTracking();
  const { pathname } = useRouter();
  const [isHowPrimeOpen, setHowPrimeIsOpen] = useState(false);
  const openPrimePage = (): void => {
    const { protocol, host } = window.location;
    window.open(`${protocol}//${host}/${PRIME_LANDING_PAGE_URL}`, "_blank");
    trackEvent({
      category: getCategoryLabel(pathname),
      action: ActionsEnum.PRIME_LIGHTBOX,
      label: trackingLabels.onClickSeeMoreBenefits({
        pathname: pathname,
      }),
    });
  };
  const { offers } = useGetSubscriptionOffers({
    offerPeriod: SubscriptionOfferPeriod.FreeTrial,
  });
  const basicOffer = useFilterOfferByType({
    offersList: offers,
    subscriptionOfferType: SubscriptionOfferType.Basic,
  });

  const handleOnMouseEnter = (setHowPrimeIsOpenValue: boolean): void => {
    setHowPrimeIsOpen(setHowPrimeIsOpenValue);
    trackEvent({
      category: getCategoryLabel(pathname),
      action: ActionsEnum.PRIME_LIGHTBOX,
      label: trackingLabels.onLoadTooltip({
        pathname: pathname,
      }),
    });
  };

  const handleOnClose = (): void => {
    trackEvent({
      category: getCategoryLabel(pathname),
      action: ActionsEnum.PRIME_LIGHTBOX,
      label: trackingLabels.onClickContinueBooking({
        pathname: pathname,
      }),
    });
    onClose();
  };

  const freeTrialDaysNumber = freeTrialDays(basicOffer?.duration);

  return (
    <Flex
      flexDirection={"column"}
      justifyContent={"center"}
      alignItems={"center"}
      mt={"small"}
    >
      <PopoverContainer>
        <div
          onMouseEnter={() => handleOnMouseEnter(true)}
          onMouseLeave={() => setHowPrimeIsOpen(false)}
          style={{ cursor: "pointer", display: "inline-block" }}
        >
          <Flex alignItems={"center"} justifyContent={"center"}>
            <Glyph
              category={"Alerts"}
              name={"InfoCircle"}
              size={"small"}
              color={"neutral.70"}
            />
            <Box ml={"2x_small"}>
              <Link variant={"legal"}>
                <Body type={"xSmall"} color={"neutral.70"}>
                  {t("lightboxPrimeRevampDesktop.informativeLink.title")}
                </Body>
              </Link>
            </Box>
          </Flex>
          <LightboxFooterPopover
            isHowPrimeOpen={isHowPrimeOpen}
            freeTrialDays={freeTrialDaysNumber}
          />
        </div>
      </PopoverContainer>
      <LightboxFooterButtons
        onClose={handleOnClose}
        openPrimePage={openPrimePage}
      />
      <Box mt={"big"}>
        <TrustPilotWidget trustPilotType={TrustPilotType.MICRO} />
      </Box>
      <Flex mt={"big"}>
        <Body
          type={"xSmall"}
          color={"neutral.70"}
          textAlign={"center"}
          data-testid={"lighbox-terms-and-conditions-link"}
        >
          <Trans
            i18nKey={"lightboxPrimeRevampDesktop.termsAndConditions.text"}
            components={{
              termsAndConditions: (
                <LinkComponent
                  text={t(
                    "lightboxPrimeRevampDesktop.termsAndConditions.label",
                  )}
                  href={t("lightboxPrimeRevampDesktop.termsAndConditions.url")}
                />
              ),
            }}
            values={{ freeTrialDays: freeTrialDaysNumber }}
          />
        </Body>
      </Flex>
    </Flex>
  );
};

export default LightboxFooter;
